import { motion } from "framer-motion";
import { Leaf, Building2, ShieldCheck, Route } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 44 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function WhyEon() {
  return (
    <section id="why-eon" data-testid="why-eon-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p {...fadeUp()} className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
          Why EON Fairfox
        </motion.p>
        <motion.h2
          {...fadeUp(0.1)}
          className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6] max-w-2xl"
        >
          What sets EON <span className="gold-text italic">apart.</span>
        </motion.h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-5">
          <motion.article
            data-testid="bento-connectivity"
            {...fadeUp(0.05)}
            className="group md:col-span-4 relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-[#D4AF37]/40 transition-colors duration-500 min-h-[380px]"
          >
            <img
              src="https://www.fairfoxitinfra.com/public/media/images/vertical/eon-noida-08-aerial-hr-1710921462.png"
              alt="Aerial render of EON on the Noida Expressway"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <Route size={22} className="text-[#D4AF37] mb-4" />
              <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#F3F4F6]">
                Three-side-open plot on the Noida Expressway.
              </h3>
              <p className="mt-3 max-w-md text-sm text-[#9CA3AF] leading-relaxed">
                Direct FNG and Noida Expressway connectivity with easy access to two major metro stations —
                the coordinates every tenant shortlists first.
              </p>
            </div>
          </motion.article>

          <motion.article
            data-testid="bento-green"
            {...fadeUp(0.12)}
            className="md:col-span-2 rounded-2xl border border-white/[0.07] bg-[#121418] p-8 flex flex-col justify-between hover:border-[#D4AF37]/40 transition-colors duration-500 min-h-[380px]"
          >
            <Leaf size={22} className="text-[#D4AF37]" />
            <div>
              <h3 className="font-display text-2xl font-semibold text-[#F3F4F6]">USGBC Gold compliant</h3>
              <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">
                A green-certified Grade-A development — engineered for lower operating costs and a healthier
                working environment.
              </p>
              <div className="mt-6 font-mono text-4xl gold-text font-medium">GOLD</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">USGBC Certification Compliant</div>
            </div>
          </motion.article>

          <motion.article
            data-testid="bento-lobby"
            {...fadeUp(0.05)}
            className="md:col-span-2 rounded-2xl border border-white/[0.07] bg-[#121418] p-8 flex flex-col justify-between hover:border-[#D4AF37]/40 transition-colors duration-500 min-h-[300px]"
          >
            <Building2 size={22} className="text-[#D4AF37]" />
            <div>
              <h3 className="font-display text-2xl font-semibold text-[#F3F4F6]">88 lifts. Triple-height lobby.</h3>
              <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">
                Eighty-eight high-speed elevators across the towers and a triple-height entrance lobby that
                sets the tone for every meeting.
              </p>
            </div>
          </motion.article>

          <motion.article
            data-testid="bento-security"
            {...fadeUp(0.12)}
            className="group md:col-span-4 relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-[#D4AF37]/40 transition-colors duration-500 min-h-[300px]"
          >
            <img
              src="/eon-plaza.webp"
              alt="EON plaza with operational retail and visitors"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <ShieldCheck size={22} className="text-[#D4AF37] mb-4" />
              <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#F3F4F6]">
                3-tier security. Parking for ~3,000 cars.
              </h3>
              <p className="mt-3 max-w-md text-sm text-[#9CA3AF] leading-relaxed">
                Multi-tier access control across the campus and one of the largest parking provisions on the
                expressway corridor.
              </p>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
